import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Spacer from 'components/ui/Spacer';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { getSuiteRooms } from 'helpers/SelectOptions';
import { ConfigProvider, Empty as AntEmpty } from 'antd';
import { getProperty } from 'actions/landlordPropertiesActions';

import {
  getProperties,
  getUnits,
  getPropertyRooms,
  getUnitRooms,
  showUnits,
} from 'actions/landlordPropertiesActions';

import { getListOfMaintenanceStaff, getAssets } from 'actions/maintenanceActions';

import { getTranslatedString } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const StyledContainer = styled.div`
  margin-bottom: 32px;
`;

const Empty = () => (
  <AntEmpty description={getTranslatedString('noData')} image={AntEmpty.PRESENTED_IMAGE_SIMPLE} />
);

const Location = ({
  formProps,
  properties,
  units,
  rooms,
  assets,
  getProperties,
  getUnits,
  getAssets,
  getPropertyRooms,
  getUnitRooms,
  getListOfMaintenanceStaff,
  readOnly,
  getProperty,
  maintenanceLocations,
  viewDetails,
}) => {
  const { values, setFieldValue, touched, errors, setValues } = formProps;
  const [showUnitField, setShowUnitField] = useState(!!values.unit_id);
  const [showAssetField, setShowAssetField] = useState(!!values.asset_id);
  const [showRoomField, setShowRoomField] = useState(!!values.room_id);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (values.unit_id) {
      getUnits(values.property_id, { for_multiselect: true });
      getAssets({ unit_id: values.unit_id, for_multiselect: true });
      getUnitRooms(values.unit_id, { for_multiselect: true });
      getListOfMaintenanceStaff(values.property_id);
    } else if (values.property_id) {
      getListOfMaintenanceStaff(values.property_id);
      getUnits(values.property_id, { for_multiselect: true });
      getAssets({ property_id: values.property_id, for_multiselect: true });
      getPropertyRooms(values.property_id, { for_multiselect: true });
    }
  }, [
    getListOfMaintenanceStaff,
    getUnits,
    getAssets,
    getUnitRooms,
    getPropertyRooms,
    values.unit_id,
    values.property_id,
  ]);

  useEffect(() => {
    if (!featureEnabled('maintenance.dynamic_locations')) {
      return;
    }

    let fetchedLocations = maintenanceLocations;
    if (values.property_id && values.unit_id) {
      // runs on unit change and filters through already fetched maintenanceCategory
      fetchedLocations = fetchedLocations.filter((category) =>
        ['suite', 'all_locations'].includes(category.ticket_location)
      );
      setLocations(fetchedLocations);
    } else {
      // runs on property change and filters through newly fetched maintenanceCategory from line 57 on this file
      fetchedLocations = fetchedLocations.filter((location) =>
        ['common_area', 'all_locations'].includes(location.ticket_location)
      );
    }

    if (
      viewDetails &&
      maintenanceLocations.length > 0 &&
      !maintenanceLocations.find((res) => res.value === values.suite_location)
    ) {
      // If mode is "edit" (viewDetails) and from the latest fetched maintenanceLocations
      // if ticket's category is not included in the latest fetched maintenanceLocations,
      // update the form value category to empty string
      // Cannot do undefined here as it will not trigger validation on the field
      // so we set to undefined when we set value to the actual field if category is an empty string
      // Refer to where we set the category field value in this file.
      setFieldValue('suite_location', '');
    }
    setLocations(fetchedLocations);
  }, [
    maintenanceLocations,
    values.property_id,
    values.unit_id,
    values.suite_location,
    viewDetails,
    setFieldValue,
  ]);

  const getPropertyType = () => {
    if (!formProps.values.property_id) return null;

    const propertyHash = properties.find(
      (property) => property.value === formProps.values.property_id
    );
    return propertyHash && propertyHash.property_type;
  };

  const handlePropertyChange = async (property_id, getProperty) => {
    setValues({
      ...values,
      property_id,
      category: undefined,
      unit_id: undefined,
      asset_id: undefined,
      room_id: undefined,
      property_option: 'property',
    });
    setShowUnitField(false);
    setShowAssetField(false);
    setShowRoomField(false);
    getProperty(property_id);
  };

  const handlePropertyOptionChange = async (value) => {
    setFieldValue('unit_id', undefined);
    setFieldValue('asset_id', undefined);
    setFieldValue('room_id', undefined);
    setFieldValue('property_option', value);

    setShowUnitField(value === 'unit');
    setShowAssetField(value === 'asset');
    setShowRoomField(value === 'room');
  };

  const handleUnitChange = (value) => {
    setFieldValue('unit_id', value);
    setFieldValue('asset_id', undefined);
    setFieldValue('room_id', undefined);
    setFieldValue('unit_option', 'unit');
    setShowAssetField(value === 'asset');
    setShowRoomField(value === 'room');
  };

  const handleUnitOptionChange = async (value) => {
    setFieldValue('asset_id', undefined);
    setFieldValue('room_id', undefined);
    setFieldValue('unit_option', value);
    setShowAssetField(value === 'asset');
    setShowRoomField(value === 'room');
  };

  const handleAssetChange = (value) => {
    setFieldValue('asset_id', value);
  };

  const handleRoomChange = (value) => {
    setFieldValue('room_id', value);
  };

  const renderPropertyField = () => {
    return (
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.maintenance.tickets.property')}*`}
              validateStatus={touched.property_id && errors.property_id ? 'error' : null}
              help={touched.property_id && errors.property_id}
            >
              <FullWidthSelect
                name="property_id"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.property_id}
                onChange={(property_id) => handlePropertyChange(property_id, getProperty)}
                disabled={readOnly}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {properties.map((property) => (
                  <Select.Option value={property.value} key={property.value}>
                    {property.label}
                  </Select.Option>
                ))}
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    );
  };

  const renderPropertyOptionField = () => {
    return (
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={`${getTranslatedString(
                'landlordPortal.maintenance.tickets.propertyOptions'
              )}*`}
              validateStatus={touched.property_option && errors.property_option ? 'error' : null}
              help={touched.property_option && errors.property_option}
            >
              <FullWidthSelect
                name="property_option"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.property_option}
                onChange={handlePropertyOptionChange}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                disabled={!values.property_id || readOnly}
              >
                <Select.Option key="property_option_1" value="property">
                  {getTranslatedString('landlordPortal.maintenance.tickets.assignToProperty')}
                </Select.Option>
                <Select.Option key="property_option_2" value="asset">
                  {getTranslatedString('landlordPortal.maintenance.tickets.assetOwnedByProperty')}
                </Select.Option>
                <Select.Option key="property_option_3" value="room">
                  {getTranslatedString('landlordPortal.maintenance.tickets.roomOwnedByProperty')}
                </Select.Option>
                <Select.Option key="property_option_4" value="unit">
                  {getTranslatedString('landlordPortal.maintenance.tickets.unitsWithinProperty')}
                </Select.Option>
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    );
  };

  const renderUnitField = () => {
    return (
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.maintenance.tickets.unit')}*`}
              validateStatus={touched.unit_id && errors.unit_id ? 'error' : null}
              help={touched.unit_id && errors.unit_id}
            >
              <FullWidthSelect
                name="unit_id"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.unit_id}
                onChange={handleUnitChange}
                disabled={readOnly}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {units.map((unit) => (
                  <Select.Option key={unit} value={unit.value}>
                    {unit.label}
                  </Select.Option>
                ))}
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    );
  };

  const renderUnitOptionField = () => {
    return (
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.maintenance.tickets.unitOptions')}*`}
              validateStatus={touched.unit_option && errors.unit_option ? 'error' : null}
              help={touched.unit_option && errors.unit_option}
            >
              <FullWidthSelect
                name="unit_option"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.unit_option}
                onChange={handleUnitOptionChange}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                disabled={!values.unit_id || readOnly}
              >
                <Select.Option key="unit_option_1" value="unit">
                  {getTranslatedString('landlordPortal.maintenance.tickets.assignToUnit')}
                </Select.Option>
                <Select.Option key="unit_option_2" value="asset">
                  {getTranslatedString('landlordPortal.maintenance.tickets.assetOwnedByUnit')}
                </Select.Option>
                <Select.Option key="unit_option_3" value="room">
                  {getTranslatedString('landlordPortal.maintenance.tickets.roomOwnedByUnit')}
                </Select.Option>
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    );
  };

  const renderAssetField = () => {
    return (
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.maintenance.tickets.asset')}*`}
              validateStatus={touched.asset_id && errors.asset_id ? 'error' : null}
              help={touched.asset_id && errors.asset_id}
            >
              <FullWidthSelect
                name="asset_id"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.asset_id}
                onChange={handleAssetChange}
                disabled={readOnly}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {assets.map((asset) => (
                  <Select.Option key={asset.value} value={asset.value}>
                    {asset.label}
                  </Select.Option>
                ))}
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    );
  };

  const renderRoomField = () => {
    return (
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.maintenance.tickets.room')}*`}
              validateStatus={touched.room_id && errors.room_id ? 'error' : null}
              help={touched.room_id && errors.room_id}
            >
              <FullWidthSelect
                name="room_id"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.room_id}
                onChange={handleRoomChange}
                disabled={readOnly}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {rooms.map((room) => (
                  <Select.Option key={room.value} value={room.value}>
                    {room.label}
                  </Select.Option>
                ))}
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    );
  };

  const renderSuiteLocationField = () => {
    return (
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.maintenance.tickets.suiteLocation')}*`}
              validateStatus={touched.suite_location && errors.suite_location ? 'error' : null}
              help={touched.suite_location && errors.suite_location}
            >
              <FullWidthSelect
                name="suite_location"
                showSearch
                placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
                size="large"
                value={values.suite_location}
                disabled={readOnly}
                onChange={(value) => setFieldValue('suite_location', value)}
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {renderSuiteLocations()}
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
    );
  };

  const renderSuiteLocations = () => {
    if (featureEnabled('maintenance.dynamic_locations')) {
      return locations.map((room) => (
        <Select.Option value={room.value} key={room.label}>
          {room.label}
        </Select.Option>
      ));
    }

    return getSuiteRooms(!!values.unit_id, getPropertyType()).map((room) => (
      <Select.Option value={room.value} key={getTranslatedString(room.label)}>
        {getTranslatedString(room.label)}
      </Select.Option>
    ));
  };

  return (
    <StyledContainer>
      <Spacer mb={0.5}>
        <FormTitle>{getTranslatedString('landlordPortal.maintenance.tickets.location')}</FormTitle>
      </Spacer>
      <ConfigProvider renderEmpty={Empty} image={Empty.PRESENTED_IMAGE_SIMPLE}>
        {renderPropertyField()}
        {renderPropertyOptionField()}
        {showUnitField && renderUnitField()}
        {showUnitField && renderUnitOptionField()}
        {showAssetField && renderAssetField()}
        {showRoomField && renderRoomField()}
        {renderSuiteLocationField()}
      </ConfigProvider>
    </StyledContainer>
  );
};

const mapStoreToProps = (store) => {
  return {
    properties: store.landlordProperties.properties,
    units: store.landlordProperties.units,
    assets: store.maintenanceReducer.maintenanceAssets,
    rooms: store.landlordProperties.rooms,
    maintenanceLocations: store.maintenanceReducer.maintenanceLocations,
  };
};

const mapDispatchToProps = {
  getUnits,
  getProperties,
  getAssets,
  getPropertyRooms,
  getUnitRooms,
  showUnits,
  getListOfMaintenanceStaff,
  getProperty,
};

export default connect(mapStoreToProps, mapDispatchToProps)(Location);
